import React, { useEffect, useState, useCallback, useRef } from 'react';
import axiosInstance from '../../AxiosInstance';
import Modal from '../../components/modal/modal';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBuilding, faLayerGroup, faWifi, faVideo, faPaperPlane,
    faPencil, faBars, faTriangleExclamation, faXmark, faExpand,
} from '@fortawesome/free-solid-svg-icons';
import useMQTTIndoor from '../../components/mqtt_service/mqtt_indoor';
import IndoorBeaconsTable from './components/indoor_beacons_table';
import IndoorBuildingManagement from './components/indoor_building_management';
import IndoorFloorMap from './components/indoor_floor_map';

// ── Rotatable floor plan overlay (replaces GroundOverlay to support rotation) ──
function makeRotatableOverlay(mapApi, imageUrl, swLL, neLL, rotation) {
    let _sw = swLL, _ne = neLL, _rot = rotation || 0, _img = null;
    const ov = new (class extends mapApi.OverlayView {
        onAdd() {
            _img = document.createElement('img');
            _img.src = imageUrl;
            _img.style.cssText = 'position:absolute;pointer-events:none;transform-origin:center center;opacity:0.75;';
            this.getPanes().overlayLayer.appendChild(_img);
        }
        draw() {
            if (!_img) return;
            const proj = this.getProjection();
            const swPx = proj.fromLatLngToDivPixel(_sw);
            const nePx = proj.fromLatLngToDivPixel(_ne);
            const l = Math.min(swPx.x, nePx.x), t = Math.min(nePx.y, swPx.y);
            const w = Math.abs(nePx.x - swPx.x), h = Math.abs(swPx.y - nePx.y);
            _img.style.left = l + 'px'; _img.style.top = t + 'px';
            _img.style.width = w + 'px'; _img.style.height = h + 'px';
            _img.style.transform = `rotate(${_rot}deg)`;
        }
        onRemove() {
            if (_img?.parentNode) { _img.parentNode.removeChild(_img); _img = null; }
        }
    })();
    ov.updateRotation = (r) => { _rot = r; if (_img) _img.style.transform = `rotate(${r}deg)`; };
    ov.updateBounds = (sw, ne) => { _sw = sw; _ne = ne; ov.draw(); };
    return ov;
}

// Compute position of rotation handle marker (placed at "top" of image at given rotation)
function computeHandlePos(swLat, swLng, neLat, neLng, rotationDeg) {
    const centerLat = (swLat + neLat) / 2, centerLng = (swLng + neLng) / 2;
    const latScale = 111111, lngScale = 111111 * Math.cos(centerLat * Math.PI / 180);
    const halfH = Math.abs(neLat - swLat) / 2 * latScale;
    const halfW = Math.abs(neLng - swLng) / 2 * lngScale;
    const dist = Math.max(halfH, halfW) * 1.5;
    const rad = rotationDeg * Math.PI / 180;
    return { lat: centerLat + dist * Math.cos(rad) / latScale, lng: centerLng + dist * Math.sin(rad) / lngScale };
}

// Compute rotation (degrees, clockwise from north) from handle position relative to center
function computeRotationFromHandle(swLat, swLng, neLat, neLng, handleLat, handleLng) {
    const centerLat = (swLat + neLat) / 2, centerLng = (swLng + neLng) / 2;
    const latScale = 111111, lngScale = 111111 * Math.cos(centerLat * Math.PI / 180);
    const dy = (handleLat - centerLat) * latScale, dx = (handleLng - centerLng) * lngScale;
    return Math.atan2(dx, dy) * 180 / Math.PI;
}

const CONFIDENCE_WARN = 0.5;
const EMPTY_SEND_STATE = {
    target_mode: 'floor',       // 'floor' | 'group' | 'select'
    target_group_id: '',
    selected_ids: [],
    selectedActionPerType: {},  // device_type_id (str) -> gps_action_id (int|null)
    overridesPerType: {},       // device_type_id (str) -> {data_1, data_2, data_3}
    expandedTypes: new Set(),
};
const inputStyle = { width: '100%', padding: '7px 10px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '13px', outline: 'none', marginBottom: '4px', boxSizing: 'border-box' };

export default function GPSIndoorContent({ project_id, mapApi = null, mapInstance = null, defaultCenter = null, defaultZoom = null }) {
    const { t } = useTranslation();

    // ── Data ──────────────────────────────────────────────────────────────────
    const [buildings, setBuildings] = useState([]);
    const [selectedBuilding, setSelectedBuilding] = useState(null);
    const [selectedFloor, setSelectedFloor] = useState(null);
    const [beacons, setBeacons] = useState([]);          // all project beacons
    const [beaconsOnFloor, setBeaconsOnFloor] = useState([]);
    const [camerasOnFloor, setCamerasOnFloor] = useState([]);
    const [indoorPositions, setIndoorPositions] = useState([]);  // IndoorPosition for current floor
    const [allCameras, setAllCameras] = useState([]);    // all IPCameras for placement modal
    const [gpsActions, setGpsActions] = useState([]);
    const [gpsAssets, setGpsAssets] = useState([]);
    const [groups, setGroups] = useState([]);

    // ── MQTT real-time ────────────────────────────────────────────────────────
    const { positions: mqttPositions } = useMQTTIndoor(project_id);

    // ── Google Maps overlay refs (used when mapMode=true) ─────────────────────
    const isMountedRef = useRef(true);
    useEffect(() => { return () => { isMountedRef.current = false; }; }, []);

    // Refs to always-current selectedBuilding/selectedFloor (avoids stale closures in callbacks)
    const selectedBuildingRef = useRef(null);
    const selectedFloorRef = useRef(null);
    selectedBuildingRef.current = selectedBuilding;
    selectedFloorRef.current = selectedFloor;

    const groundOverlayRef = useRef(null);
    const beaconMarkersRef = useRef([]);
    const cameraMarkersRef = useRef([]);
    const peopleMarkersRef = useRef([]);
    const cornerMarkersRef = useRef([]);
    const infoWindowsRef = useRef([]);
    const gpsAssetsRef = useRef([]);
    gpsAssetsRef.current = gpsAssets;
    const [alignMode, setAlignMode] = useState(false);
    const [alignRotation, setAlignRotation] = useState(0);
    const alignRotationRef = useRef(0);
    const [collapsed, setCollapsed] = useState(false);
    const [showFloorMapModal, setShowFloorMapModal] = useState(false);

    // ── UI state ──────────────────────────────────────────────────────────────
    const [selectedPerson, setSelectedPerson] = useState(null);

    const [showBuildingsModal, setShowBuildingsModal] = useState(false);
    const [showBeaconsModal, setShowBeaconsModal] = useState(false);
    const [showSendModal, setShowSendModal] = useState(false);

    // Edit mode: null | 'beacon' | 'camera'
    const [editMode, setEditMode] = useState(null);
    const [pendingItem, setPendingItem] = useState(null);   // item selected from picker
    const [pendingPlacement, setPendingPlacement] = useState(null);  // {pos_x, pos_y}

    // Picker modals (select beacon/camera to place)
    const [showBeaconPicker, setShowBeaconPicker] = useState(false);
    const [showCameraPicker, setShowCameraPicker] = useState(false);
    const [pickerSearch, setPickerSearch] = useState('');
    const [expandedMachines, setExpandedMachines] = useState(new Set());
    const [manualX, setManualX] = useState('');
    const [manualY, setManualY] = useState('');
    const [buildingSearch, setBuildingSearch] = useState('');

    // Send action
    const [sendState, setSendState] = useState(EMPTY_SEND_STATE);
    const [sendLoading, setSendLoading] = useState(false);
    const [sendResult, setSendResult] = useState(null);
    const [selectSearch, setSelectSearch] = useState('');

    // Camera global exclusion across floors
    const [allBuildingCameraIds, setAllBuildingCameraIds] = useState(new Set());

    // ── Fetch ─────────────────────────────────────────────────────────────────
    const fetchBuildingCameras = useCallback((building) => {
        if (!building) { setAllBuildingCameraIds(new Set()); return; }
        axiosInstance.get(`floor_camera/?building_id=${building.id}`).then(res => {
            if (!isMountedRef.current) return;
            setAllBuildingCameraIds(new Set(res.data.map(fc => fc.camera)));
        }).catch(() => {});
    }, []);

    const fetchBuildings = useCallback(() => {
        axiosInstance.get(`building/?project_id=${project_id}`).then(res => {
            if (!isMountedRef.current) return;
            const data = res.data;
            setBuildings(data);
            const curBuilding = selectedBuildingRef.current;
            const curFloor = selectedFloorRef.current;
            if (curBuilding) {
                // Restore previously selected building/floor if they still exist
                const prev = data.find(b => b.id === curBuilding.id);
                if (prev) {
                    setSelectedBuilding(prev);
                    const prevFloor = curFloor ? prev.floors?.find(f => f.id === curFloor.id) : null;
                    setSelectedFloor(prevFloor || null);
                } else {
                    setSelectedBuilding(null);
                    setSelectedFloor(null);
                }
            }
        });
    }, [project_id]);

    const fetchFloorData = useCallback((floorId) => {
        if (!floorId) return;
        axiosInstance.get(`floor_beacon/?floor_id=${floorId}`).then(r => { if (isMountedRef.current) setBeaconsOnFloor(r.data); });
        axiosInstance.get(`floor_camera/?floor_id=${floorId}`).then(r => { if (isMountedRef.current) setCamerasOnFloor(r.data); });
        axiosInstance.get(`indoor_position/?floor_id=${floorId}`).then(r => { if (isMountedRef.current) setIndoorPositions(r.data); });
    }, []);

    useEffect(() => { fetchBuildings(); }, [fetchBuildings]);

    useEffect(() => {
        let isMounted = true;
        axiosInstance.get(`indoor_beacon/?project_id=${project_id}`).then(r => { if (isMounted) setBeacons(r.data); });
        axiosInstance.get(`ip_camera/?project_id=${project_id}`).then(r => { if (isMounted) setAllCameras(Array.isArray(r.data) ? r.data : (r.data.results || [])); }).catch(() => {});
        axiosInstance.get(`gps_action/?project_id=${project_id}`).then(r => { if (isMounted) setGpsActions(Array.isArray(r.data) ? r.data : []); }).catch(() => {});
        axiosInstance.get(`gps_asset/?project_id=${project_id}`).then(r => { if (isMounted) setGpsAssets(Array.isArray(r.data) ? r.data : []); }).catch(() => {});
        axiosInstance.get(`gps_asset_group/?project_id=${project_id}`).then(r => { if (isMounted) setGroups(Array.isArray(r.data) ? r.data : []); }).catch(() => {});
        return () => { isMounted = false; };
    }, [project_id]);

    useEffect(() => {
        if (selectedFloor) fetchFloorData(selectedFloor.id);
        else { setBeaconsOnFloor([]); setCamerasOnFloor([]); setIndoorPositions([]); }
    }, [selectedFloor, fetchFloorData]);

    // Merge MQTT real-time positions with API snapshot
    const mergedPeople = indoorPositions.map(ip => {
        const rt = mqttPositions[ip.device_user];
        if (rt && rt.floor_id === selectedFloor?.id) {
            return { ...ip, position_x: rt.position_x, position_y: rt.position_y, confidence: rt.confidence };
        }
        return ip;
    });
    // Also add people that arrived via MQTT but weren't in the initial API snapshot for this floor
    Object.entries(mqttPositions).forEach(([duId, pos]) => {
        if (pos.floor_id === selectedFloor?.id && !indoorPositions.find(ip => ip.device_user === parseInt(duId))) {
            mergedPeople.push({ device_user: parseInt(duId), device_user_name: `#${duId}`, position_x: pos.position_x, position_y: pos.position_y, confidence: pos.confidence, floor: selectedFloor.id, is_human: true });
        }
    });

    // ── Floor selection ───────────────────────────────────────────────────────
    const deselectBuilding = () => {
        setSelectedBuilding(null);
        setSelectedFloor(null);
        setEditMode(null);
        setPendingPlacement(null);
        setPendingItem(null);
        setSelectedPerson(null);
        setAllBuildingCameraIds(new Set());
    };

    // Navigate to default view after building is deselected (runs after ground overlay cleanup)
    useEffect(() => {
        if (!selectedBuilding && mapInstance && defaultCenter) {
            mapInstance.panTo(defaultCenter);
            if (defaultZoom) mapInstance.setZoom(defaultZoom);
        }
    }, [selectedBuilding]); // eslint-disable-line

    const selectBuilding = (b) => {
        setSelectedBuilding(b);
        setSelectedFloor(b.floors?.[0] || null);
        setEditMode(null);
        setPendingPlacement(null);
        setPendingItem(null);
        setSelectedPerson(null);
        fetchBuildingCameras(b);
    };

    const selectFloor = (f) => {
        setSelectedFloor(f);
        setEditMode(null);
        setPendingPlacement(null);
        setPendingItem(null);
        setSelectedPerson(null);
    };

    // ── Placement flow ────────────────────────────────────────────────────────
    const startPlaceBeacon = (beacon) => {
        setPendingItem(beacon);
        setEditMode('beacon');
        setShowBeaconPicker(false);
        setPendingPlacement(null);
        setManualX('');
        setManualY('');
    };

    const startPlaceCamera = (camera) => {
        setPendingItem(camera);
        setEditMode('camera');
        setShowCameraPicker(false);
        setPendingPlacement(null);
        setManualX('');
        setManualY('');
    };

    const confirmPlacement = (coords) => {
        if (!pendingItem || !selectedFloor) return;
        const pos_x = parseFloat(coords.pos_x);
        const pos_y = parseFloat(coords.pos_y);
        if (isNaN(pos_x) || isNaN(pos_y)) return;

        if (editMode === 'beacon') {
            axiosInstance.post('floor_beacon/', { floor: selectedFloor.id, beacon: pendingItem.id, position_x: pos_x, position_y: pos_y })
                .then(() => { fetchFloorData(selectedFloor.id); });
        } else if (editMode === 'camera') {
            axiosInstance.post('floor_camera/', { floor: selectedFloor.id, camera: pendingItem.id, position_x: pos_x, position_y: pos_y })
                .then(() => { fetchFloorData(selectedFloor.id); fetchBuildingCameras(selectedBuildingRef.current); });
        }
        setEditMode(null);
        setPendingItem(null);
        setPendingPlacement(null);
    };

    const confirmManualPlacement = () => {
        const x = parseFloat(manualX);
        const y = parseFloat(manualY);
        if (!isNaN(x) && !isNaN(y) && x >= 0 && x <= 100 && y >= 0 && y <= 100) {
            confirmPlacement({ pos_x: x, pos_y: y });
        }
    };

    const removeFromFloor = (type, id) => {
        const endpoint = type === 'beacon' ? `floor_beacon/${id}/` : `floor_camera/${id}/`;
        axiosInstance.delete(endpoint).then(() => {
            fetchFloorData(selectedFloor.id);
            if (type === 'camera') fetchBuildingCameras(selectedBuildingRef.current);
        });
    };

    const handleReposition = (type, item, coords) => {
        const endpoint = type === 'beacon' ? `floor_beacon/${item.id}/` : `floor_camera/${item.id}/`;
        axiosInstance.patch(endpoint, { position_x: coords.pos_x, position_y: coords.pos_y })
            .then(() => fetchFloorData(selectedFloor.id));
    };

    // Auto-open floor plan modal when entering placement mode
    useEffect(() => {
        if (editMode && selectedFloor) setShowFloorMapModal(true);
    }, [editMode]); // eslint-disable-line

    // ── Send Action ───────────────────────────────────────────────────────────
    const getTargetGroups = () => {
        let targetIds = [];
        if (sendState.target_mode === 'floor') {
            targetIds = mergedPeople.map(ip => ip.device_user);
        } else if (sendState.target_mode === 'group') {
            const group = groups.find(g => String(g.id) === String(sendState.target_group_id));
            if (group) {
                const memberIds = new Set(group.members?.map(m => m.device_user_id) || []);
                targetIds = mergedPeople.filter(ip => memberIds.has(ip.device_user)).map(ip => ip.device_user);
            }
        } else if (sendState.target_mode === 'select') {
            targetIds = sendState.selected_ids;
        }

        const byType = {};
        for (const duId of targetIds) {
            const asset = gpsAssets.find(a => a.id === duId);
            const dtId = asset?.active_gps_device?.device_type_id;
            const dtName = asset?.active_gps_device?.device_type_name || t('gps.assets.unknownType');
            if (dtId == null) continue;
            const key = String(dtId);
            if (!byType[key]) byType[key] = { device_type_id: key, device_type_name: dtName, asset_ids: [] };
            byType[key].asset_ids.push(duId);
        }
        return Object.values(byType);
    };

    const handleSendAction = async () => {
        if (sendLoading) return;
        setSendLoading(true);
        setSendResult(null);

        if (sendState.target_mode === 'group' && !sendState.target_group_id) {
            setSendResult({ error: t('gps.assets.noGroupSelected') });
            setSendLoading(false);
            return;
        }

        const targetGroups = getTargetGroups();
        if (targetGroups.length === 0) {
            setSendResult({ error: t('gps.assets.noTargetsSelected') });
            setSendLoading(false);
            return;
        }

        for (const group of targetGroups) {
            if (!sendState.selectedActionPerType[group.device_type_id]) {
                setSendResult({ error: t('gps.assets.noActionSelected', { deviceType: group.device_type_name }) });
                setSendLoading(false);
                return;
            }
        }

        let totalExecuted = 0;
        const allErrors = [];
        try {
            for (const group of targetGroups) {
                const actionId = sendState.selectedActionPerType[group.device_type_id];
                const overrides = sendState.overridesPerType[group.device_type_id] || {};
                const res = await axiosInstance.post('gps_asset/send_action/', {
                    gps_action_id: actionId,
                    asset_ids: group.asset_ids,
                    group_ids: [],
                    ...(overrides.data_1 !== undefined ? { data_1_override: overrides.data_1 } : {}),
                    ...(overrides.data_2 !== undefined ? { data_2_override: overrides.data_2 } : {}),
                    ...(overrides.data_3 !== undefined ? { data_3_override: overrides.data_3 } : {}),
                });
                totalExecuted += res.data.executed || 0;
                if (res.data.errors?.length) allErrors.push(...res.data.errors);
            }
            setSendResult({ executed: totalExecuted, errors: allErrors });
            if (!allErrors.length) setTimeout(() => setShowSendModal(false), 1200);
        } catch (e) {
            setSendResult({ error: e.response?.data?.error || 'Error' });
        }
        setSendLoading(false);
    };

    // ── Cameras grouped by machine/resource for picker ────────────────────────
    const camerasByMachine = allCameras.reduce((acc, cam) => {
        const m = cam.machine_name || cam.device_user_name || 'Other';
        if (!acc[m]) acc[m] = [];
        acc[m].push(cam);
        return acc;
    }, {});
    // Filter by search (machine name match → show all cams; partial cam match → show only matching cams)
    const filteredCameraGroups = Object.entries(camerasByMachine).reduce((acc, [machine, cams]) => {
        const q = pickerSearch.toLowerCase().trim();
        if (!q) { acc[machine] = cams; return acc; }
        const machineMatch = machine.toLowerCase().includes(q);
        const matchingCams = machineMatch ? cams : cams.filter(c => c.name?.toLowerCase().includes(q));
        if (matchingCams.length) acc[machine] = matchingCams;
        return acc;
    }, {});

    const toggleMachine = (machine) => {
        setExpandedMachines(prev => {
            const next = new Set(prev);
            next.has(machine) ? next.delete(machine) : next.add(machine);
            return next;
        });
    };

    const alreadyPlacedBeaconIds = new Set(beaconsOnFloor.map(fb => fb.beacon));
    const alreadyPlacedCameraIds = new Set(camerasOnFloor.map(fc => fc.camera));
    const availableBeacons = beacons.filter(b => !alreadyPlacedBeaconIds.has(b.id));

    const currentFloorFull = selectedBuilding?.floors?.find(f => f.id === selectedFloor?.id) || selectedFloor;

    // ── Helper: convert % position to lat/lng using floor bounds (with rotation) ─
    const posToLatLng = useCallback((pos_x, pos_y, floor) => {
        if (!floor?.map_bounds_sw_lat) return null;
        const centerLat = (floor.map_bounds_sw_lat + floor.map_bounds_ne_lat) / 2;
        const centerLng = (floor.map_bounds_sw_lng + floor.map_bounds_ne_lng) / 2;
        const latScale = 111111;
        const lngScale = 111111 * Math.cos(centerLat * Math.PI / 180);
        const halfH = ((floor.map_bounds_ne_lat - floor.map_bounds_sw_lat) / 2) * latScale;
        const halfW = ((floor.map_bounds_ne_lng - floor.map_bounds_sw_lng) / 2) * lngScale;
        // Local position in meters from center (x: east, y: north)
        const mx = (pos_x / 100 - 0.5) * 2 * halfW;
        const my = (0.5 - pos_y / 100) * 2 * halfH;
        // Apply clockwise rotation
        const rad = ((floor.map_rotation || 0) * Math.PI) / 180;
        const cos = Math.cos(rad), sin = Math.sin(rad);
        const rx = mx * cos - my * sin;
        const ry = mx * sin + my * cos;
        return { lat: centerLat + ry / latScale, lng: centerLng + rx / lngScale };
    }, []);

    // ── Rotatable overlay management ──────────────────────────────────────────
    useEffect(() => {
        if (!mapApi || !mapInstance) return;

        if (groundOverlayRef.current) { groundOverlayRef.current.setMap(null); groundOverlayRef.current = null; }

        const floor = currentFloorFull;
        if (!floor?.floor_plan_url || !floor.map_bounds_sw_lat) return;

        const swLL = new mapApi.LatLng(floor.map_bounds_sw_lat, floor.map_bounds_sw_lng);
        const neLL = new mapApi.LatLng(floor.map_bounds_ne_lat, floor.map_bounds_ne_lng);
        const overlay = makeRotatableOverlay(mapApi, floor.floor_plan_url, swLL, neLL, floor.map_rotation || 0);
        overlay.setMap(mapInstance);
        groundOverlayRef.current = overlay;

        const bounds = new mapApi.LatLngBounds(swLL, neLL);
        const center = bounds.getCenter();
        mapInstance.panTo(center);
        const maxZoomService = new mapApi.MaxZoomService();
        maxZoomService.getMaxZoomAtLatLng(center, (result) => {
            mapInstance.setZoom(result.status === mapApi.MaxZoomStatus.OK ? result.zoom : 22);
        });

        return () => {
            if (groundOverlayRef.current) { groundOverlayRef.current.setMap(null); groundOverlayRef.current = null; }
        };
    }, [mapApi, mapInstance, selectedFloor, currentFloorFull]); // eslint-disable-line

    // ── Beacon / camera markers ───────────────────────────────────────────────
    useEffect(() => {
        if (!mapApi || !mapInstance) return;
        beaconMarkersRef.current.forEach(m => m.setMap(null)); beaconMarkersRef.current = [];
        cameraMarkersRef.current.forEach(m => m.setMap(null)); cameraMarkersRef.current = [];
        infoWindowsRef.current.forEach(iw => iw.close()); infoWindowsRef.current = [];
        const floor = currentFloorFull;
        if (!floor?.map_bounds_sw_lat) return;

        beaconsOnFloor.forEach(fb => {
            const pos = posToLatLng(fb.position_x, fb.position_y, floor);
            if (!pos) return;
            const iw = new mapApi.InfoWindow({
                content: `<div style="font-size:13px;padding:4px 6px;min-width:170px;line-height:1.6"><b style="color:#4F46E5">${fb.beacon_name || 'Beacon'}</b><br/><span style="font-family:monospace;font-size:11px;color:#64748B">${fb.beacon_mac || ''}</span><br/>RSSI@1m: <b>${fb.rssi_at_1m ?? '?'} dBm</b>&nbsp;&nbsp;TX: <b>${fb.tx_power ?? '?'} dBm</b></div>`
            });
            const m = new mapApi.Marker({ position: pos, map: mapInstance, title: fb.beacon_name || fb.beacon_mac,
                icon: { path: mapApi.SymbolPath.CIRCLE, scale: 9, fillColor: '#4F46E5', fillOpacity: 0.9, strokeColor: '#fff', strokeWeight: 2 } });
            m.addListener('click', () => { infoWindowsRef.current.forEach(i => i.close()); iw.open(mapInstance, m); });
            beaconMarkersRef.current.push(m);
            infoWindowsRef.current.push(iw);
        });
        camerasOnFloor.forEach(fc => {
            const pos = posToLatLng(fc.position_x, fc.position_y, floor);
            if (!pos) return;
            const iw = new mapApi.InfoWindow({
                content: `<div style="font-size:13px;padding:4px 6px;min-width:160px;line-height:1.6"><b style="color:#0F766E">${fc.camera_name}</b>${fc.machine_name ? `<br/><span style="color:#64748B;font-size:11px">${fc.machine_name}</span>` : ''}<br/><span style="color:#94A3B8;font-size:11px">Open "Floor Plan" to view stream</span></div>`
            });
            const m = new mapApi.Marker({ position: pos, map: mapInstance, title: fc.camera_name,
                icon: { path: mapApi.SymbolPath.CIRCLE, scale: 9, fillColor: '#0F766E', fillOpacity: 0.9, strokeColor: '#fff', strokeWeight: 2 } });
            m.addListener('click', () => { infoWindowsRef.current.forEach(i => i.close()); iw.open(mapInstance, m); });
            cameraMarkersRef.current.push(m);
            infoWindowsRef.current.push(iw);
        });
        return () => {
            beaconMarkersRef.current.forEach(m => m.setMap(null));
            cameraMarkersRef.current.forEach(m => m.setMap(null));
            infoWindowsRef.current.forEach(iw => iw.close());
        };
    }, [mapApi, mapInstance, beaconsOnFloor, camerasOnFloor, selectedFloor, currentFloorFull, posToLatLng]); // eslint-disable-line

    // ── People markers (real-time) ────────────────────────────────────────────
    useEffect(() => {
        if (!mapApi || !mapInstance) return;
        peopleMarkersRef.current.forEach(m => m.setMap(null)); peopleMarkersRef.current = [];
        const floor = currentFloorFull;
        if (!floor?.map_bounds_sw_lat) return;
        mergedPeople.forEach(ip => {
            const pos = posToLatLng(ip.position_x, ip.position_y, floor);
            if (!pos) return;
            const m = new mapApi.Marker({ position: pos, map: mapInstance, title: ip.device_user_name,
                icon: { path: mapApi.SymbolPath.CIRCLE, scale: 10, fillColor: ip.is_human ? '#10B981' : '#F59E0B', fillOpacity: 0.95, strokeColor: '#fff', strokeWeight: 2 } });
            m.addListener('click', () => {
                infoWindowsRef.current.forEach(iw => iw.close());
                const asset = gpsAssetsRef.current.find(a => a.id === ip.device_user);
                const battery = asset?.active_gps_device?.battery != null ? Math.round(asset.active_gps_device.battery) : null;
                const lastMsg = asset?.active_gps_device?.last_message;
                const content = `<div style="font-family:sans-serif;min-width:180px">
                    <div style="font-weight:600;font-size:14px;margin-bottom:4px">${ip.device_user_name}</div>
                    <div style="font-size:12px;color:#64748B">
                        <div>${ip.is_human ? 'Person' : 'Asset'}</div>
                        ${asset?.identification_number ? `<div>Doc: ${asset.identification_number}</div>` : ''}
                        ${battery != null ? `<div style="margin-top:2px">Battery: ${battery}%</div>` : ''}
                        ${asset?.active_gps_device ? `<div>Device: ${asset.active_gps_device.name}</div>` : ''}
                        ${lastMsg ? `<div>Last msg: ${new Date(lastMsg).toLocaleString()}</div>` : ''}
                        <div style="margin-top:2px">Confidence: ${Math.round((ip.confidence || 0) * 100)}%</div>
                    </div>
                    <div style="margin-top:8px">
                        <a href="https://www.google.com/maps/dir/?api=1&destination=${pos.lat},${pos.lng}" target="_blank" rel="noopener noreferrer"
                           style="display:block;padding:6px 0;text-align:center;background:#1E293B;color:white;border-radius:6px;font-size:12px;font-weight:600;text-decoration:none">
                            Go to location
                        </a>
                    </div>
                </div>`;
                const iw = new mapApi.InfoWindow({ content, position: pos, pixelOffset: new mapApi.Size(0, -20) });
                iw.open(mapInstance);
                infoWindowsRef.current.push(iw);
            });
            peopleMarkersRef.current.push(m);
        });
        return () => { peopleMarkersRef.current.forEach(m => m.setMap(null)); };
    }, [mapApi, mapInstance, mergedPeople, selectedFloor, currentFloorFull, posToLatLng]); // eslint-disable-line

    // ── Cleanup all on unmount ────────────────────────────────────────────────
    useEffect(() => {
        return () => {
            if (groundOverlayRef.current) groundOverlayRef.current.setMap(null);
            beaconMarkersRef.current.forEach(m => m.setMap(null));
            cameraMarkersRef.current.forEach(m => m.setMap(null));
            peopleMarkersRef.current.forEach(m => m.setMap(null));
            cornerMarkersRef.current.forEach(m => m.setMap(null));
            infoWindowsRef.current.forEach(iw => iw.close());
        };
    }, []); // eslint-disable-line

    // ── Align mode: drag SW/NE/Rotate markers to georeference floor plan ───────
    const startAlignMode = () => {
        if (!mapApi || !mapInstance || !currentFloorFull?.floor_plan_url) return;
        const floor = currentFloorFull;
        const center = mapInstance.getCenter();
        const defaultDelta = 0.0008;
        const swLat = floor.map_bounds_sw_lat ?? (center.lat() - defaultDelta);
        const swLng = floor.map_bounds_sw_lng ?? (center.lng() - defaultDelta);
        const neLat = floor.map_bounds_ne_lat ?? (center.lat() + defaultDelta);
        const neLng = floor.map_bounds_ne_lng ?? (center.lng() + defaultDelta);
        const initRotation = floor.map_rotation || 0;
        alignRotationRef.current = initRotation;
        setAlignRotation(initRotation);

        const pinIcon = (color) => ({
            path: mapApi.SymbolPath.CIRCLE, scale: 12,
            fillColor: color, fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2,
        });

        const swMarker = new mapApi.Marker({ position: { lat: swLat, lng: swLng }, map: mapInstance,
            draggable: true, title: 'SW corner', label: { text: '↙', color: '#fff', fontSize: '14px' },
            icon: pinIcon('#EF4444'), zIndex: 1000 });
        const neMarker = new mapApi.Marker({ position: { lat: neLat, lng: neLng }, map: mapInstance,
            draggable: true, title: 'NE corner', label: { text: '↗', color: '#fff', fontSize: '14px' },
            icon: pinIcon('#22C55E'), zIndex: 1000 });

        // Rotation handle marker: placed at top of overlay (bearing = rotation from north)
        const initHandlePos = computeHandlePos(swLat, swLng, neLat, neLng, initRotation);
        const rotHandle = new mapApi.Marker({ position: initHandlePos, map: mapInstance,
            draggable: true, title: 'Drag to rotate',
            label: { text: '↻', color: '#fff', fontSize: '16px' },
            icon: pinIcon('#8B5CF6'), zIndex: 1001 });

        // Create/update overlay without recreating every drag
        if (groundOverlayRef.current) { groundOverlayRef.current.setMap(null); groundOverlayRef.current = null; }
        const ov = makeRotatableOverlay(mapApi, floor.floor_plan_url,
            new mapApi.LatLng(swLat, swLng), new mapApi.LatLng(neLat, neLng), initRotation);
        ov.setMap(mapInstance);
        groundOverlayRef.current = ov;

        const refreshBounds = () => {
            const sw = swMarker.getPosition(); const ne = neMarker.getPosition();
            groundOverlayRef.current?.updateBounds(sw, ne);
        };
        const refreshHandlePos = () => {
            const sw = swMarker.getPosition(); const ne = neMarker.getPosition();
            rotHandle.setPosition(computeHandlePos(sw.lat(), sw.lng(), ne.lat(), ne.lng(), alignRotationRef.current));
        };

        swMarker.addListener('drag', () => { refreshBounds(); refreshHandlePos(); });
        neMarker.addListener('drag', () => { refreshBounds(); refreshHandlePos(); });
        rotHandle.addListener('drag', () => {
            const sw = swMarker.getPosition(); const ne = neMarker.getPosition();
            const hp = rotHandle.getPosition();
            const newRot = computeRotationFromHandle(sw.lat(), sw.lng(), ne.lat(), ne.lng(), hp.lat(), hp.lng());
            alignRotationRef.current = newRot;
            setAlignRotation(parseFloat(newRot.toFixed(1)));
            groundOverlayRef.current?.updateRotation(newRot);
        });

        cornerMarkersRef.current = [swMarker, neMarker, rotHandle];
        setAlignMode(true);
    };

    const rotateBy = (delta) => {
        const [swMarker, neMarker, rotHandle] = cornerMarkersRef.current;
        if (!swMarker || !rotHandle) return;
        const newRot = alignRotationRef.current + delta;
        alignRotationRef.current = newRot;
        setAlignRotation(parseFloat(newRot.toFixed(1)));
        groundOverlayRef.current?.updateRotation(newRot);
        const sw = swMarker.getPosition(); const ne = neMarker.getPosition();
        rotHandle.setPosition(computeHandlePos(sw.lat(), sw.lng(), ne.lat(), ne.lng(), newRot));
    };

    const haversineMeters = (lat1, lng1, lat2, lng2) => {
        const R = 6371000;
        const toRad = d => d * Math.PI / 180;
        const dLat = toRad(lat2 - lat1);
        const dLng = toRad(lng2 - lng1);
        const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    };

    const saveAlignment = async () => {
        const [swMarker, neMarker] = cornerMarkersRef.current;
        if (!swMarker || !neMarker) return;
        const sw = swMarker.getPosition(); const ne = neMarker.getPosition();
        const width_meters = parseFloat(haversineMeters(sw.lat(), sw.lng(), sw.lat(), ne.lng()).toFixed(1));
        const height_meters = parseFloat(haversineMeters(sw.lat(), sw.lng(), ne.lat(), sw.lng()).toFixed(1));
        await axiosInstance.patch(`building_floor/${selectedFloorRef.current.id}/`, {
            map_bounds_sw_lat: sw.lat(), map_bounds_sw_lng: sw.lng(),
            map_bounds_ne_lat: ne.lat(), map_bounds_ne_lng: ne.lng(),
            width_meters,
            height_meters,
            map_rotation: alignRotationRef.current,
        });
        cornerMarkersRef.current.forEach(m => m.setMap(null)); cornerMarkersRef.current = [];
        setAlignMode(false);
        setAlignRotation(0);
        fetchBuildings();
    };

    const cancelAlignment = () => {
        cornerMarkersRef.current.forEach(m => m.setMap(null)); cornerMarkersRef.current = [];
        setAlignMode(false);
        setAlignRotation(0);
    };

    // ── Controls section (unified) ────────────────────────────────────────────
    const controlsSection = selectedFloor && (
        <div style={{ padding: '8px 12px 12px', borderTop: '1px solid #E2E8F0' }}>
            {alignMode ? (
                <div>
                    <div style={{ fontSize: '11px', color: '#64748B', marginBottom: '5px' }}>
                        Drag <b style={{color:'#EF4444'}}>↙SW</b> and <b style={{color:'#22C55E'}}>↗NE</b> pins to move/resize.
                        Drag <b style={{color:'#8B5CF6'}}>↻</b> pin or use buttons to rotate.
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '5px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '11px', color: '#8B5CF6', whiteSpace: 'nowrap', minWidth: '52px' }}>↻ {alignRotation.toFixed(1)}°</span>
                        <button onClick={() => rotateBy(-15)} style={smallBtn('#8B5CF6')}>-15°</button>
                        <button onClick={() => rotateBy(-5)} style={smallBtn('#8B5CF6')}>-5°</button>
                        <button onClick={() => rotateBy(5)} style={smallBtn('#8B5CF6')}>+5°</button>
                        <button onClick={() => rotateBy(15)} style={smallBtn('#8B5CF6')}>+15°</button>
                        <button onClick={() => rotateBy(-alignRotationRef.current)} style={smallBtn('#94A3B8')} title="Reset rotation">0°</button>
                    </div>
                    <div style={{ display: 'flex', gap: '5px' }}>
                        <button onClick={saveAlignment} style={{ ...smallBtn('#22C55E'), flex: 1 }}>Save</button>
                        <button onClick={cancelAlignment} style={{ ...smallBtn('#EF4444'), flex: 1 }}>Cancel</button>
                    </div>
                </div>
            ) : editMode ? (
                <div>
                    <div style={{ fontSize: '11px', color: '#64748B', marginBottom: '5px' }}>
                        Enter position on floor plan (0–100%)
                    </div>
                    <div style={{ display: 'flex', gap: '5px', marginBottom: '5px' }}>
                        <input type="number" value={manualX} onChange={e => setManualX(e.target.value)}
                            placeholder="X %" style={{ ...inputStyle, marginBottom: 0, flex: 1 }} />
                        <input type="number" value={manualY} onChange={e => setManualY(e.target.value)}
                            placeholder="Y %" style={{ ...inputStyle, marginBottom: 0, flex: 1 }} />
                        <button onClick={confirmManualPlacement} style={smallBtn('#22C55E')}>OK</button>
                    </div>
                    <button onClick={() => { setEditMode(null); setPendingItem(null); setPendingPlacement(null); }}
                        style={{ ...smallBtn('#EF4444'), width: '100%' }}>Cancel</button>
                </div>
            ) : (
                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                    {currentFloorFull?.floor_plan_url && (
                        <button onClick={() => setShowFloorMapModal(true)} style={smallBtn('#334155')}
                            title="View floor plan">
                            <FontAwesomeIcon icon={faExpand} /> Floor Plan
                        </button>
                    )}
                    {currentFloorFull?.floor_plan_url ? (
                        <button onClick={startAlignMode} style={smallBtn('#F59E0B')}>
                            📍 {currentFloorFull.map_bounds_sw_lat ? 'Adjust location' : 'Set on map'}
                        </button>
                    ) : (
                        <span style={{ fontSize: '11px', color: '#94A3B8' }}>Upload floor plan first</span>
                    )}
                    <button onClick={() => { setPickerSearch(''); setShowBeaconPicker(true); }} style={smallBtn('#4F46E5')}
                        title={t('gps.indoor.addBeaconToFloor')}>
                        <BluetoothIcon />
                    </button>
                    <button onClick={() => { setPickerSearch(''); setExpandedMachines(new Set()); setShowCameraPicker(true); }} style={smallBtn('#0F766E')}
                        title={t('gps.indoor.addCameraToFloor')}>
                        <FontAwesomeIcon icon={faVideo} />
                    </button>
                </div>
            )}
        </div>
    );

    // ── Modals (shared) ───────────────────────────────────────────────────────
    const modals = (
        <>
            <Modal show={showBuildingsModal} setter={setShowBuildingsModal}
                name={t('gps.indoor.manageBuildings')} hideCancel buttonName={t('common.close', 'Close')}
                onClick={() => setShowBuildingsModal(false)}>
                <IndoorBuildingManagement buildings={buildings} projectId={project_id} onRefresh={fetchBuildings} />
            </Modal>
            <Modal show={showBeaconsModal} setter={setShowBeaconsModal}
                name={t('gps.indoor.manageBeacons')} hideCancel buttonName={t('common.close', 'Close')}
                onClick={() => setShowBeaconsModal(false)}>
                <IndoorBeaconsTable beacons={beacons} projectId={project_id}
                    onRefresh={() => axiosInstance.get(`indoor_beacon/?project_id=${project_id}`).then(r => setBeacons(r.data))} />
            </Modal>
            <Modal show={showBeaconPicker} setter={setShowBeaconPicker}
                name={t('gps.indoor.selectBeacon')} hideCancel buttonName={t('common.close', 'Close')}
                onClick={() => setShowBeaconPicker(false)}>
                <input value={pickerSearch} onChange={e => setPickerSearch(e.target.value)}
                    placeholder={t('gps.indoor.searchBeacon')} style={{ ...inputStyle, marginBottom: '10px' }} />
                {beacons.filter(b => !pickerSearch || b.name?.toLowerCase().includes(pickerSearch.toLowerCase()) || b.mac_address?.toLowerCase().includes(pickerSearch.toLowerCase())).map(b => {
                    const isPlaced = alreadyPlacedBeaconIds.has(b.id);
                    const floorBeacon = beaconsOnFloor.find(fb => fb.beacon === b.id);
                    return (
                        <label key={b.id} style={{ padding: '9px 12px', borderRadius: '7px', border: `1px solid ${isPlaced ? '#4F46E5' : '#E2E8F0'}`, marginBottom: '6px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: isPlaced ? '#EEF2FF' : '' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <input type="checkbox" checked={isPlaced}
                                    onChange={() => { if (isPlaced) removeFromFloor('beacon', floorBeacon.id); else startPlaceBeacon(b); }}
                                    style={{ accentColor: '#4F46E5', width: '15px', height: '15px', cursor: 'pointer', flexShrink: 0 }} />
                                <div>
                                    <div style={{ fontWeight: '600', fontSize: '13px', color: '#1E293B' }}>{b.name}</div>
                                    <div style={{ fontSize: '11px', color: '#64748B', fontFamily: 'monospace' }}>{b.mac_address}</div>
                                </div>
                            </div>
                            <div style={{ fontSize: '11px', color: '#94A3B8' }}>{b.rssi_at_1m} dBm</div>
                        </label>
                    );
                })}
                {beacons.length === 0 && <div style={{ fontSize: '13px', color: '#94A3B8' }}>{t('gps.indoor.noBeaconsInProject')}</div>}
            </Modal>
            <Modal show={showCameraPicker} setter={setShowCameraPicker}
                name={t('gps.indoor.selectCamera')} hideCancel buttonName={t('common.close', 'Close')}
                onClick={() => setShowCameraPicker(false)}>
                <input
                    value={pickerSearch}
                    onChange={e => {
                        setPickerSearch(e.target.value);
                        // Auto-expand machines that match search
                        if (e.target.value.trim()) {
                            const q = e.target.value.toLowerCase();
                            const matching = Object.keys(camerasByMachine).filter(m =>
                                m.toLowerCase().includes(q) || camerasByMachine[m].some(c => c.name?.toLowerCase().includes(q))
                            );
                            setExpandedMachines(new Set(matching));
                        } else {
                            setExpandedMachines(new Set());
                        }
                    }}
                    placeholder={t('gps.indoor.searchByMachine')}
                    style={{ ...inputStyle, marginBottom: '12px' }}
                />
                {Object.keys(filteredCameraGroups).length === 0 && (
                    <div style={{ fontSize: '13px', color: '#94A3B8', textAlign: 'center', padding: '16px 0' }}>No cameras found.</div>
                )}
                {Object.entries(filteredCameraGroups).map(([machine, cams]) => {
                    // Show cameras on this floor + cameras not placed anywhere in the building or outdoor map
                    const visible = cams.filter(c => (!allBuildingCameraIds.has(c.id) && !c.has_map_placement) || alreadyPlacedCameraIds.has(c.id));
                    const placedCount = visible.filter(c => alreadyPlacedCameraIds.has(c.id)).length;
                    const isExpanded = expandedMachines.has(machine);
                    if (visible.length === 0) return null;
                    return (
                        <div key={machine} style={{ marginBottom: '4px', border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden' }}>
                            {/* Machine header - clickable to expand */}
                            <div
                                onClick={() => toggleMachine(machine)}
                                style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    padding: '10px 14px', cursor: 'pointer', userSelect: 'none',
                                    backgroundColor: isExpanded ? '#F1F5F9' : '#F8FAFC',
                                    borderBottom: isExpanded ? '1px solid #E2E8F0' : 'none',
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FontAwesomeIcon icon={faBuilding} style={{ color: '#64748B', fontSize: '12px' }} />
                                    <span style={{ fontWeight: '600', fontSize: '13px', color: '#334155' }}>{machine}</span>
                                    <span style={{ fontSize: '11px', color: '#94A3B8', backgroundColor: '#E2E8F0', borderRadius: '10px', padding: '1px 7px' }}>
                                        {placedCount}/{visible.length}
                                    </span>
                                </div>
                                <span style={{ color: '#94A3B8', fontSize: '12px' }}>{isExpanded ? '▲' : '▼'}</span>
                            </div>
                            {/* Camera list - shown when expanded */}
                            {isExpanded && (
                                <div style={{ padding: '6px 8px' }}>
                                    {visible.map(c => {
                                        const isPlaced = alreadyPlacedCameraIds.has(c.id);
                                        const floorCamera = camerasOnFloor.find(fc => fc.camera === c.id);
                                        return (
                                            <label key={c.id}
                                                style={{ padding: '8px 10px', borderRadius: '6px', marginBottom: '3px', cursor: 'pointer', fontSize: '13px', color: '#1E293B', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px', background: isPlaced ? '#F0FDF4' : '', border: isPlaced ? '1px solid #BBF7D0' : '1px solid transparent' }}
                                                onMouseOver={e => e.currentTarget.style.background = isPlaced ? '#DCFCE7' : '#EEF2FF'}
                                                onMouseOut={e => e.currentTarget.style.background = isPlaced ? '#F0FDF4' : ''}>
                                                <input type="checkbox" checked={isPlaced}
                                                    onChange={() => { if (isPlaced) removeFromFloor('camera', floorCamera.id); else startPlaceCamera(c); }}
                                                    style={{ accentColor: '#0F766E', width: '14px', height: '14px', cursor: 'pointer', flexShrink: 0 }} />
                                                <FontAwesomeIcon icon={faVideo} style={{ color: '#0F766E', fontSize: '12px' }} />
                                                {c.name}
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </Modal>
            <Modal show={showSendModal} setter={setShowSendModal}
                name={t('gps.indoor.sendAction')}
                buttonName={sendLoading ? t('gps.assets.sending') : t('gps.indoor.sendAction')}
                onClick={handleSendAction}>
                {(() => {
                    const targetGroups = getTargetGroups();
                    const multipleTypes = targetGroups.length > 1;

                    const renderActionSelector = (dtId, dtName, dtAssetIds) => {
                        const actionsForType = gpsActions.filter(a => String(a.device_type) === String(dtId));
                        const selectedId = sendState.selectedActionPerType[String(dtId)];
                        const selectedAction = actionsForType.find(a => a.id === selectedId);
                        const overrides = sendState.overridesPerType[String(dtId)] || {};
                        const expanded = sendState.expandedTypes.has(String(dtId));

                        const setSelectedAction = (id) => {
                            const action = actionsForType.find(a => a.id === parseInt(id));
                            setSendState(s => ({
                                ...s,
                                selectedActionPerType: { ...s.selectedActionPerType, [String(dtId)]: id ? parseInt(id) : null },
                                overridesPerType: { ...s.overridesPerType, [String(dtId)]: action ? { data_1: action.data_1 || '', data_2: action.data_2 || '', data_3: action.data_3 || '' } : {} },
                            }));
                        };

                        const setOverride = (field, value) => {
                            setSendState(s => ({
                                ...s,
                                overridesPerType: { ...s.overridesPerType, [String(dtId)]: { ...(s.overridesPerType[String(dtId)] || {}), [field]: value } },
                            }));
                        };

                        const toggleExpanded = () => {
                            setSendState(s => {
                                const next = new Set(s.expandedTypes);
                                if (next.has(String(dtId))) next.delete(String(dtId));
                                else next.add(String(dtId));
                                return { ...s, expandedTypes: next };
                            });
                        };

                        return (
                            <div key={dtId} style={{ marginBottom: '12px', padding: '12px', border: '1px solid #E2E8F0', borderRadius: '8px', background: '#FAFAFA' }}>
                                {multipleTypes && (
                                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '8px' }}>
                                        {dtName} <span style={{ color: '#94A3B8', fontWeight: 400 }}>({dtAssetIds.length} {dtAssetIds.length === 1 ? t('gps.assets.asset') : t('gps.assets.assetsLower')})</span>
                                    </div>
                                )}
                                <select value={selectedId || ''} onChange={e => setSelectedAction(e.target.value)} style={inputStyle}>
                                    <option value="">{t('gps.assets.selectAction')}</option>
                                    {actionsForType.length === 0 && <option disabled>{t('gps.assets.noActionsForType')}</option>}
                                    {actionsForType.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                </select>
                                {selectedAction && (
                                    <div style={{ marginTop: '6px' }}>
                                        <button type="button" onClick={toggleExpanded}
                                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '12px', color: '#4F46E5', fontWeight: '500' }}>
                                            {expanded ? t('gps.assets.hideDetails') : t('gps.assets.showDetails')}
                                        </button>
                                        {expanded && (
                                            <div style={{ marginTop: '8px', padding: '10px', background: 'white', border: '1px solid #E2E8F0', borderRadius: '6px' }}>
                                                {(selectedAction.action_type === 5 || selectedAction.action_type === 1) && (<>
                                                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#64748B', marginBottom: '3px' }}>
                                                        {selectedAction.action_type === 5 ? t('gps.assets.topic') : t('gps.assets.emailAddress')}
                                                    </div>
                                                    <input value={overrides.data_1 !== undefined ? overrides.data_1 : (selectedAction.data_1 || '')}
                                                        onChange={e => setOverride('data_1', e.target.value)}
                                                        style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '12px' }} />
                                                </>)}
                                                {selectedAction.action_type === 6 && (<>
                                                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#64748B', marginBottom: '3px' }}>{t('gps.assets.endpointPath')}</div>
                                                    <input value={overrides.data_1 !== undefined ? overrides.data_1 : (selectedAction.data_1 || '')}
                                                        onChange={e => setOverride('data_1', e.target.value)}
                                                        style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '12px' }} />
                                                </>)}
                                                {(selectedAction.action_type === 5 || selectedAction.action_type === 6) && (<>
                                                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#64748B', marginBottom: '3px', marginTop: '8px' }}>
                                                        {selectedAction.action_type === 5 ? t('gps.assets.payload') : t('gps.assets.bodyJson')}
                                                    </div>
                                                    <textarea value={overrides.data_3 !== undefined ? overrides.data_3 : (selectedAction.data_3 || '')}
                                                        onChange={e => setOverride('data_3', e.target.value)}
                                                        style={{ ...inputStyle, minHeight: '70px', resize: 'vertical', fontFamily: 'monospace', fontSize: '12px' }} />
                                                </>)}
                                                {selectedAction.action_type === 1 && (<>
                                                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#64748B', marginBottom: '3px', marginTop: '8px' }}>{t('gps.assets.subject')}</div>
                                                    <input value={overrides.data_2 !== undefined ? overrides.data_2 : (selectedAction.data_2 || '')}
                                                        onChange={e => setOverride('data_2', e.target.value)}
                                                        style={{ ...inputStyle, fontSize: '12px' }} />
                                                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#64748B', marginBottom: '3px', marginTop: '8px' }}>{t('gps.assets.body')}</div>
                                                    <textarea value={overrides.data_3 !== undefined ? overrides.data_3 : (selectedAction.data_3 || '')}
                                                        onChange={e => setOverride('data_3', e.target.value)}
                                                        style={{ ...inputStyle, minHeight: '60px', resize: 'vertical', fontSize: '12px' }} />
                                                </>)}
                                                {(selectedAction.action_type === 3 || selectedAction.action_type === 4) && (<>
                                                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#64748B', marginBottom: '3px' }}>{t('gps.assets.phoneNumber')}</div>
                                                    <input value={overrides.data_1 !== undefined ? overrides.data_1 : (selectedAction.data_1 || '')}
                                                        onChange={e => setOverride('data_1', e.target.value)}
                                                        style={{ ...inputStyle, fontSize: '12px' }} />
                                                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#64748B', marginBottom: '3px', marginTop: '8px' }}>{t('gps.assets.message')}</div>
                                                    <textarea value={overrides.data_3 !== undefined ? overrides.data_3 : (selectedAction.data_3 || '')}
                                                        onChange={e => setOverride('data_3', e.target.value)}
                                                        style={{ ...inputStyle, minHeight: '60px', resize: 'vertical', fontSize: '12px' }} />
                                                </>)}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    };

                    return (
                        <div>
                            {/* Target mode */}
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '10px' }}>{t('gps.assets.targets')}</div>
                            {[
                                { v: 'floor', l: `All on floor (${mergedPeople.length})` },
                                { v: 'group', l: t('gps.assets.byGroup') },
                                { v: 'select', l: t('gps.assets.selectIndividually') },
                            ].map(opt => (
                                <label key={opt.v} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer', fontSize: '13px' }}>
                                    <input type="radio" name="indoor_send_target" value={opt.v}
                                        checked={sendState.target_mode === opt.v}
                                        onChange={() => { setSendState(s => ({ ...s, target_mode: opt.v })); setSelectSearch(''); }}
                                        style={{ accentColor: '#4F46E5' }} />
                                    {opt.l}
                                </label>
                            ))}

                            {sendState.target_mode === 'group' && (
                                <select value={sendState.target_group_id}
                                    onChange={e => setSendState(s => ({ ...s, target_group_id: e.target.value }))}
                                    style={inputStyle}>
                                    <option value="">{t('gps.assets.selectGroup')}</option>
                                    {groups.map(g => {
                                        const memberIds = new Set(g.members?.map(m => m.device_user_id) || []);
                                        const onFloor = mergedPeople.filter(ip => memberIds.has(ip.device_user)).length;
                                        return <option key={g.id} value={g.id}>{g.name} ({onFloor} on floor)</option>;
                                    })}
                                </select>
                            )}

                            {sendState.target_mode === 'select' && (
                                <>
                                    <input
                                        value={selectSearch}
                                        onChange={e => setSelectSearch(e.target.value)}
                                        placeholder="Search by name or ID…"
                                        style={{ ...inputStyle, marginBottom: '5px' }}
                                    />
                                    <div style={{ border: '1px solid #E2E8F0', borderRadius: '6px', overflow: 'hidden', maxHeight: '160px', overflowY: 'auto' }}>
                                        {mergedPeople.filter(ip => {
                                            if (!selectSearch) return true;
                                            const q = selectSearch.toLowerCase();
                                            const asset = gpsAssets.find(a => a.id === ip.device_user);
                                            return ip.device_user_name?.toLowerCase().includes(q) ||
                                                String(asset?.identification_number ?? '').toLowerCase().includes(q);
                                        }).map(ip => {
                                            const asset = gpsAssets.find(a => a.id === ip.device_user);
                                            return (
                                                <label key={ip.device_user} style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '7px 10px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #F1F5F9' }}>
                                                    <input type="checkbox"
                                                        checked={sendState.selected_ids.includes(ip.device_user)}
                                                        onChange={() => setSendState(s => {
                                                            const ids = s.selected_ids;
                                                            return { ...s, selected_ids: ids.includes(ip.device_user) ? ids.filter(i => i !== ip.device_user) : [...ids, ip.device_user] };
                                                        })}
                                                        style={{ accentColor: '#4F46E5' }} />
                                                    <div>
                                                        <div>{ip.device_user_name}</div>
                                                        {asset?.identification_number && (
                                                            <div style={{ fontSize: '11px', color: '#94A3B8' }}>{asset.identification_number}</div>
                                                        )}
                                                    </div>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </>
                            )}

                            <div style={{ borderTop: '1px solid #E2E8F0', margin: '14px 0 12px' }} />

                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '10px' }}>{t('gps.assets.action')}</div>

                            {targetGroups.length === 0 && (
                                <div style={{ fontSize: '13px', color: '#94A3B8', padding: '8px 0' }}>{t('gps.assets.selectTargetsFirst')}</div>
                            )}

                            {multipleTypes && (
                                <div style={{ marginBottom: '12px', padding: '8px 12px', borderRadius: '6px', background: '#FFFBEB', border: '1px solid #FDE68A', fontSize: '12px', color: '#92400E' }}>
                                    {t('gps.assets.multipleDeviceTypesWarning', { count: targetGroups.length })}
                                </div>
                            )}

                            {targetGroups.map(g => renderActionSelector(g.device_type_id, g.device_type_name, g.asset_ids))}

                            {sendResult && (
                                <div style={{ marginTop: '10px', padding: '9px 12px', borderRadius: '6px', background: sendResult.error ? '#FEF2F2' : '#F0FDF4', border: `1px solid ${sendResult.error ? '#FECACA' : '#BBF7D0'}`, fontSize: '13px', color: sendResult.error ? '#DC2626' : '#16A34A' }}>
                                    {sendResult.error
                                        ? sendResult.error
                                        : `${t('gps.assets.actionSentTo')} ${sendResult.executed} person(s)${sendResult.errors?.length ? ` (${sendResult.errors.length} error${sendResult.errors.length !== 1 ? 's' : ''})` : ''}`
                                    }
                                    {sendResult.errors?.length > 0 && (
                                        <ul style={{ margin: '6px 0 0', paddingLeft: '16px' }}>
                                            {sendResult.errors.map((e, i) => <li key={i}>{e.asset}: {e.error}</li>)}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })()}
            </Modal>
        </>
    );

    // ── Render: collapsible card embedded in gps_map side panel ──────────────
    return (
    <>
        <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
            {/* Header */}
            <div
                onClick={() => setCollapsed(v => !v)}
                style={{
                    padding: '10px 12px',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    cursor: 'pointer',
                    borderBottom: collapsed ? 'none' : '1px solid #E2E8F0',
                    fontWeight: '700', fontSize: '13px', color: '#1E293B',
                    userSelect: 'none',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FontAwesomeIcon icon={faBuilding} style={{ color: '#4F46E5' }} />
                    Indoor
                    {mergedPeople.length > 0 && (
                        <span style={{ fontSize: '10px', background: '#4F46E5', color: '#fff', borderRadius: '10px', padding: '1px 7px' }}>
                            {mergedPeople.length}
                        </span>
                    )}
                </div>
                <span style={{ color: '#94A3B8', fontSize: '11px' }}>{collapsed ? '▼' : '▲'}</span>
            </div>

            {!collapsed && (
                <>
                    {/* Building selector */}
                    <div style={{ padding: '10px 12px', borderBottom: '1px solid #F1F5F9' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '7px', flexWrap: 'wrap', gap: '5px' }}>
                            <span style={{ fontSize: '11px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase' }}>
                                {t('gps.indoor.buildings')}
                            </span>
                            <button onClick={() => setShowBuildingsModal(true)} style={smallBtn('#4F46E5')}>
                                <FontAwesomeIcon icon={faPencil} /> {t('gps.indoor.manageBuildings')}
                            </button>
                        </div>
                        {buildings.length === 0 ? (
                            <div style={{ fontSize: '12px', color: '#94A3B8' }}>{t('gps.indoor.noBuildings')}</div>
                        ) : (
                            <>
                                {buildings.length > 4 && (
                                    <input
                                        type="text"
                                        value={buildingSearch}
                                        onChange={e => setBuildingSearch(e.target.value)}
                                        placeholder="Search building..."
                                        style={{ ...inputStyle, marginBottom: '7px', padding: '5px 8px', fontSize: '12px' }}
                                    />
                                )}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                    {buildings
                                        .filter(b => !buildingSearch || b.name.toLowerCase().includes(buildingSearch.toLowerCase()))
                                        .map(b => (
                                            <button key={b.id} onClick={() => selectedBuilding?.id === b.id ? deselectBuilding() : selectBuilding(b)}
                                                style={{ ...tabBtn, ...(selectedBuilding?.id === b.id ? tabActive : {}) }}>
                                                <FontAwesomeIcon icon={faBuilding} style={{ fontSize: '10px' }} /> {b.name}
                                            </button>
                                        ))}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Floor tabs */}
                    {selectedBuilding && (
                        <div style={{ padding: '8px 12px', borderBottom: '1px solid #F1F5F9' }}>
                            <div style={{ fontSize: '11px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', marginBottom: '6px' }}>
                                {t('gps.indoor.floors')}
                            </div>
                            {selectedBuilding.floors?.length === 0 ? (
                                <div style={{ fontSize: '12px', color: '#94A3B8' }}>{t('gps.indoor.noFloors')}</div>
                            ) : (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                    {selectedBuilding.floors?.map(f => (
                                        <button key={f.id} onClick={() => selectFloor(f)}
                                            style={{ ...tabBtn, ...(selectedFloor?.id === f.id ? tabActive : {}) }}>
                                            <FontAwesomeIcon icon={faLayerGroup} style={{ fontSize: '10px' }} />
                                            {f.floor_number}: {f.name}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* People on this floor */}
                    <div style={{ padding: '8px 12px', borderBottom: selectedFloor ? '1px solid #F1F5F9' : 'none' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap', gap: '5px' }}>
                            <span style={{ fontSize: '11px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase' }}>
                                People on floor ({mergedPeople.length})
                            </span>
                            {mergedPeople.length > 0 && (
                                <button onClick={() => { setSendState(EMPTY_SEND_STATE); setSendResult(null); setShowSendModal(true); }} style={smallBtn('#16A34A')}>
                                    <FontAwesomeIcon icon={faPaperPlane} /> {t('gps.indoor.sendAction')}
                                </button>
                            )}
                        </div>
                        {mergedPeople.length === 0 ? (
                            <div style={{ fontSize: '12px', color: '#94A3B8' }}>{t('gps.indoor.noPeopleOnFloor')}</div>
                        ) : (
                            mergedPeople.map(ip => (
                                <div key={ip.device_user}
                                    onClick={() => setSelectedPerson(selectedPerson?.device_user === ip.device_user ? null : ip)}
                                    style={{
                                        padding: '6px 8px', borderRadius: '6px', marginBottom: '4px', cursor: 'pointer',
                                        border: `1px solid ${selectedPerson?.device_user === ip.device_user ? '#4F46E5' : '#E2E8F0'}`,
                                        background: selectedPerson?.device_user === ip.device_user ? '#EEF2FF' : '#F8FAFC',
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    }}>
                                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#1E293B' }}>{ip.device_user_name}</span>
                                    <ConfidenceBadge value={ip.confidence} t={t} />
                                </div>
                            ))
                        )}
                    </div>

                    {/* Controls */}
                    {controlsSection}
                </>
            )}

            {modals}
        </div>

        {/* ── Manage Beacons (independent section) ── */}
        <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #E2E8F0', marginTop: '8px' }}>
            <button
                onClick={() => setShowBeaconsModal(true)}
                style={{ width: '100%', background: 'none', border: 'none', padding: '10px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '700', color: '#1E293B' }}
            >
                <FontAwesomeIcon icon={faBars} style={{ color: '#64748B' }} />
                {t('gps.indoor.manageBeacons')}
            </button>
        </div>

        {/* ── Full-screen floor plan modal ── */}
        {showFloorMapModal && (
            <FloorPlanModal
                floor={currentFloorFull}
                beacons={beaconsOnFloor}
                cameras={camerasOnFloor}
                indoorPositions={indoorPositions}
                mqttPositions={mqttPositions}
                editMode={editMode}
                setEditMode={setEditMode}
                pendingItem={pendingItem}
                setPendingItem={setPendingItem}
                pendingPlacement={pendingPlacement}
                setPendingPlacement={setPendingPlacement}
                onPlaceItem={confirmPlacement}
                onReposition={handleReposition}
                onRemove={removeFromFloor}
                onClose={() => {
                    setShowFloorMapModal(false);
                    if (editMode) { setEditMode(null); setPendingItem(null); setPendingPlacement(null); }
                }}
                buildings={buildings}
                selectedBuilding={selectedBuilding}
                onSelectBuilding={selectBuilding}
                selectedFloor={selectedFloor}
                onSelectFloor={selectFloor}
                mergedPeople={mergedPeople}
                gpsAssets={gpsAssets}
                gpsActions={gpsActions}
                groups={groups}
            />
        )}
    </>
    );
}

// ── Small sub-components ─────────────────────────────────────────────────────

function ConfidenceBadge({ value, t }) {
    const pct = Math.round((value ?? 0) * 100);
    const low = value < CONFIDENCE_WARN;
    return (
        <span style={{
            fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: '600',
            background: low ? '#FEF3C7' : '#DCFCE7',
            color: low ? '#D97706' : '#15803D',
        }}>
            {low && <FontAwesomeIcon icon={faTriangleExclamation} style={{ marginRight: '3px' }} />}
            {pct}%
        </span>
    );
}

const tabBtn = { background: '#F1F5F9', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '5px 10px', fontSize: '12px', cursor: 'pointer', color: '#64748B', display: 'flex', alignItems: 'center', gap: '5px' };
const tabActive = { background: '#1E293B', color: '#fff', border: '1px solid #1E293B', fontWeight: '600' };
const smallBtn = (bg) => ({ background: bg, border: 'none', borderRadius: '6px', padding: '5px 10px', color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '5px' });

function BluetoothIcon({ size = 12, color = 'white' }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={{ flexShrink: 0 }}>
            <path d="M17.71 7.71L12 2h-1v7.59L6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 11 14.41V22h1l5.71-5.71-4.3-4.29 4.3-4.29zM13 5.83l1.88 1.88L13 9.59V5.83zm1.88 10.46L13 18.17v-3.76l1.88 1.88z"/>
        </svg>
    );
}

// ── Full-screen floor plan modal ──────────────────────────────────────────────
function FloorPlanModal({
    floor, beacons, cameras, indoorPositions, mqttPositions,
    editMode, setEditMode, pendingItem, setPendingItem, pendingPlacement, setPendingPlacement,
    onPlaceItem, onReposition, onRemove, onClose,
    buildings, selectedBuilding, onSelectBuilding, selectedFloor, onSelectFloor,
    mergedPeople, gpsAssets, gpsActions, groups,
}) {
    const { t } = useTranslation();
    const [sendState, setSendState] = useState({ ...EMPTY_SEND_STATE });
    const [sendLoading, setSendLoading] = useState(false);
    const [sendResult, setSendResult] = useState(null);
    const [selectSearch, setSelectSearch] = useState('');

    const mdInput = {
        width: '100%', padding: '6px 8px', borderRadius: '5px',
        border: '1px solid #334155', fontSize: '12px', outline: 'none',
        boxSizing: 'border-box', background: '#0F172A', color: '#E2E8F0', marginBottom: '4px',
    };

    const getTargetGroups = () => {
        let targetIds = [];
        if (sendState.target_mode === 'floor') {
            targetIds = mergedPeople.map(ip => ip.device_user);
        } else if (sendState.target_mode === 'group') {
            const group = groups.find(g => String(g.id) === String(sendState.target_group_id));
            if (group) {
                const memberIds = new Set(group.members?.map(m => m.device_user_id) || []);
                targetIds = mergedPeople.filter(ip => memberIds.has(ip.device_user)).map(ip => ip.device_user);
            }
        } else if (sendState.target_mode === 'select') {
            targetIds = sendState.selected_ids;
        }
        const byType = {};
        for (const duId of targetIds) {
            const asset = gpsAssets.find(a => a.id === duId);
            const dtId = asset?.active_gps_device?.device_type_id;
            const dtName = asset?.active_gps_device?.device_type_name || t('gps.assets.unknownType');
            if (dtId == null) continue;
            const key = String(dtId);
            if (!byType[key]) byType[key] = { device_type_id: key, device_type_name: dtName, asset_ids: [] };
            byType[key].asset_ids.push(duId);
        }
        return Object.values(byType);
    };

    const handleSendAction = async () => {
        if (sendLoading) return;
        setSendLoading(true);
        setSendResult(null);
        if (sendState.target_mode === 'group' && !sendState.target_group_id) {
            setSendResult({ error: t('gps.assets.noGroupSelected') }); setSendLoading(false); return;
        }
        const tGroups = getTargetGroups();
        if (tGroups.length === 0) {
            setSendResult({ error: t('gps.assets.noTargetsSelected') }); setSendLoading(false); return;
        }
        for (const group of tGroups) {
            if (!sendState.selectedActionPerType[group.device_type_id]) {
                setSendResult({ error: t('gps.assets.noActionSelected', { deviceType: group.device_type_name }) });
                setSendLoading(false); return;
            }
        }
        let totalExecuted = 0;
        const allErrors = [];
        try {
            for (const group of tGroups) {
                const actionId = sendState.selectedActionPerType[group.device_type_id];
                const overrides = sendState.overridesPerType[group.device_type_id] || {};
                const res = await axiosInstance.post('gps_asset/send_action/', {
                    gps_action_id: actionId, asset_ids: group.asset_ids, group_ids: [],
                    ...(overrides.data_1 !== undefined ? { data_1_override: overrides.data_1 } : {}),
                    ...(overrides.data_2 !== undefined ? { data_2_override: overrides.data_2 } : {}),
                    ...(overrides.data_3 !== undefined ? { data_3_override: overrides.data_3 } : {}),
                });
                totalExecuted += res.data.executed || 0;
                if (res.data.errors?.length) allErrors.push(...res.data.errors);
            }
            setSendResult({ executed: totalExecuted, errors: allErrors });
        } catch (e) {
            setSendResult({ error: e.response?.data?.error || 'Error' });
        }
        setSendLoading(false);
    };

    const targetGroups = getTargetGroups();
    const multipleTypes = targetGroups.length > 1;

    const renderActionSelector = (dtId, dtName, dtAssetIds) => {
        const actionsForType = gpsActions.filter(a => String(a.device_type) === String(dtId));
        const selectedId = sendState.selectedActionPerType[String(dtId)];
        const selectedAction = actionsForType.find(a => a.id === selectedId);
        const overrides = sendState.overridesPerType[String(dtId)] || {};
        const expanded = sendState.expandedTypes.has(String(dtId));
        const setSelectedAction = (id) => {
            const action = actionsForType.find(a => a.id === parseInt(id));
            setSendState(s => ({
                ...s,
                selectedActionPerType: { ...s.selectedActionPerType, [String(dtId)]: id ? parseInt(id) : null },
                overridesPerType: { ...s.overridesPerType, [String(dtId)]: action ? { data_1: action.data_1 || '', data_2: action.data_2 || '', data_3: action.data_3 || '' } : {} },
            }));
        };
        const setOverride = (field, value) => setSendState(s => ({
            ...s, overridesPerType: { ...s.overridesPerType, [String(dtId)]: { ...(s.overridesPerType[String(dtId)] || {}), [field]: value } },
        }));
        const toggleExpanded = () => setSendState(s => {
            const next = new Set(s.expandedTypes);
            next.has(String(dtId)) ? next.delete(String(dtId)) : next.add(String(dtId));
            return { ...s, expandedTypes: next };
        });
        return (
            <div key={dtId} style={{ marginBottom: '8px', padding: '10px', border: '1px solid #334155', borderRadius: '8px', background: '#0F172A' }}>
                {multipleTypes && (
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#94A3B8', marginBottom: '5px' }}>
                        {dtName} <span style={{ color: '#64748B', fontWeight: 400 }}>({dtAssetIds.length})</span>
                    </div>
                )}
                <select value={selectedId || ''} onChange={e => setSelectedAction(e.target.value)} style={mdInput}>
                    <option value="">{t('gps.assets.selectAction')}</option>
                    {actionsForType.length === 0 && <option disabled>{t('gps.assets.noActionsForType')}</option>}
                    {actionsForType.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                {selectedAction && (
                    <div style={{ marginTop: '4px' }}>
                        <button type="button" onClick={toggleExpanded} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '11px', color: '#818CF8' }}>
                            {expanded ? t('gps.assets.hideDetails') : t('gps.assets.showDetails')}
                        </button>
                        {expanded && (
                            <div style={{ marginTop: '6px', padding: '8px', background: '#1E293B', border: '1px solid #334155', borderRadius: '6px' }}>
                                {(selectedAction.action_type === 5 || selectedAction.action_type === 1) && (<>
                                    <div style={{ fontSize: '10px', fontWeight: '600', color: '#64748B', marginBottom: '3px' }}>
                                        {selectedAction.action_type === 5 ? t('gps.assets.topic') : t('gps.assets.emailAddress')}
                                    </div>
                                    <input value={overrides.data_1 !== undefined ? overrides.data_1 : (selectedAction.data_1 || '')} onChange={e => setOverride('data_1', e.target.value)} style={mdInput} />
                                </>)}
                                {selectedAction.action_type === 6 && (<>
                                    <div style={{ fontSize: '10px', fontWeight: '600', color: '#64748B', marginBottom: '3px' }}>{t('gps.assets.endpointPath')}</div>
                                    <input value={overrides.data_1 !== undefined ? overrides.data_1 : (selectedAction.data_1 || '')} onChange={e => setOverride('data_1', e.target.value)} style={mdInput} />
                                </>)}
                                {(selectedAction.action_type === 5 || selectedAction.action_type === 6) && (<>
                                    <div style={{ fontSize: '10px', fontWeight: '600', color: '#64748B', marginBottom: '3px', marginTop: '6px' }}>
                                        {selectedAction.action_type === 5 ? t('gps.assets.payload') : t('gps.assets.bodyJson')}
                                    </div>
                                    <textarea value={overrides.data_3 !== undefined ? overrides.data_3 : (selectedAction.data_3 || '')} onChange={e => setOverride('data_3', e.target.value)} style={{ ...mdInput, minHeight: '60px', resize: 'vertical', fontFamily: 'monospace' }} />
                                </>)}
                                {selectedAction.action_type === 1 && (<>
                                    <div style={{ fontSize: '10px', fontWeight: '600', color: '#64748B', marginBottom: '3px', marginTop: '6px' }}>{t('gps.assets.subject')}</div>
                                    <input value={overrides.data_2 !== undefined ? overrides.data_2 : (selectedAction.data_2 || '')} onChange={e => setOverride('data_2', e.target.value)} style={mdInput} />
                                    <div style={{ fontSize: '10px', fontWeight: '600', color: '#64748B', marginBottom: '3px', marginTop: '6px' }}>{t('gps.assets.body')}</div>
                                    <textarea value={overrides.data_3 !== undefined ? overrides.data_3 : (selectedAction.data_3 || '')} onChange={e => setOverride('data_3', e.target.value)} style={{ ...mdInput, minHeight: '55px', resize: 'vertical' }} />
                                </>)}
                                {(selectedAction.action_type === 3 || selectedAction.action_type === 4) && (<>
                                    <div style={{ fontSize: '10px', fontWeight: '600', color: '#64748B', marginBottom: '3px' }}>{t('gps.assets.phoneNumber')}</div>
                                    <input value={overrides.data_1 !== undefined ? overrides.data_1 : (selectedAction.data_1 || '')} onChange={e => setOverride('data_1', e.target.value)} style={mdInput} />
                                    <div style={{ fontSize: '10px', fontWeight: '600', color: '#64748B', marginBottom: '3px', marginTop: '6px' }}>{t('gps.assets.message')}</div>
                                    <textarea value={overrides.data_3 !== undefined ? overrides.data_3 : (selectedAction.data_3 || '')} onChange={e => setOverride('data_3', e.target.value)} style={{ ...mdInput, minHeight: '55px', resize: 'vertical' }} />
                                </>)}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9000, display: 'flex' }}>
            {/* ── Left sidebar ── */}
            <div style={{ width: '300px', background: '#1E293B', display: 'flex', flexDirection: 'column', borderRight: '1px solid #334155', overflowY: 'auto' }}>

                {/* Header */}
                <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #334155', flexShrink: 0 }}>
                    <span style={{ color: '#fff', fontWeight: '700', fontSize: '15px' }}>📐 Indoor</span>
                    <button onClick={onClose} style={{ background: '#475569', border: 'none', borderRadius: '6px', padding: '5px 12px', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>

                {/* Building & Floor selectors */}
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #334155', flexShrink: 0 }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', marginBottom: '5px' }}>{t('gps.indoor.buildings')}</div>
                    <select value={selectedBuilding?.id || ''} onChange={e => { const b = buildings.find(b => String(b.id) === e.target.value); if (b) onSelectBuilding(b); }} style={mdInput}>
                        <option value="">Select building…</option>
                        {buildings.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                    {selectedBuilding && (<>
                        <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', marginBottom: '5px', marginTop: '10px' }}>{t('gps.indoor.floors')}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {selectedBuilding.floors?.map(f => (
                                <button key={f.id} onClick={() => onSelectFloor(f)} style={{
                                    background: selectedFloor?.id === f.id ? '#4F46E5' : '#334155',
                                    border: 'none', borderRadius: '5px', padding: '4px 10px',
                                    color: selectedFloor?.id === f.id ? '#fff' : '#CBD5E1',
                                    cursor: 'pointer', fontSize: '12px', fontWeight: '600',
                                }}>
                                    {f.floor_number}: {f.name}
                                </button>
                            ))}
                        </div>
                    </>)}
                </div>

                {/* People on floor */}
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #334155', flexShrink: 0 }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', marginBottom: '8px' }}>
                        People on floor ({mergedPeople.length})
                    </div>
                    {mergedPeople.length === 0 ? (
                        <div style={{ fontSize: '12px', color: '#475569' }}>{t('gps.indoor.noPeopleOnFloor')}</div>
                    ) : (
                        <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: 'left', fontWeight: '600', color: '#64748B', padding: '2px 4px' }}>Name</th>
                                        <th style={{ textAlign: 'right', fontWeight: '600', color: '#64748B', padding: '2px 4px' }}>Conf.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {mergedPeople.map(ip => (
                                        <tr key={ip.device_user}>
                                            <td style={{ color: '#E2E8F0', padding: '4px 4px', fontWeight: '500' }}>{ip.device_user_name}</td>
                                            <td style={{ textAlign: 'right', padding: '4px 4px' }}><ConfidenceBadge value={ip.confidence} t={t} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Send Action */}
                <div style={{ padding: '12px 16px', flex: 1, overflowY: 'auto' }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', marginBottom: '10px' }}>
                        {t('gps.indoor.sendAction')}
                    </div>

                    {/* Target mode */}
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#94A3B8', marginBottom: '6px' }}>{t('gps.assets.targets')}</div>
                    {[
                        { v: 'floor', l: `All on floor (${mergedPeople.length})` },
                        { v: 'group', l: t('gps.assets.byGroup') },
                        { v: 'select', l: t('gps.assets.selectIndividually') },
                    ].map(opt => (
                        <label key={opt.v} style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '5px', cursor: 'pointer', fontSize: '12px', color: '#CBD5E1' }}>
                            <input type="radio" name="modal_send_target" value={opt.v}
                                checked={sendState.target_mode === opt.v}
                                onChange={() => { setSendState(s => ({ ...s, target_mode: opt.v })); setSelectSearch(''); }}
                                style={{ accentColor: '#818CF8' }} />
                            {opt.l}
                        </label>
                    ))}

                    {sendState.target_mode === 'group' && (
                        <select value={sendState.target_group_id} onChange={e => setSendState(s => ({ ...s, target_group_id: e.target.value }))} style={{ ...mdInput, marginBottom: '8px' }}>
                            <option value="">{t('gps.assets.selectGroup')}</option>
                            {groups.map(g => {
                                const memberIds = new Set(g.members?.map(m => m.device_user_id) || []);
                                const onFloor = mergedPeople.filter(ip => memberIds.has(ip.device_user)).length;
                                return <option key={g.id} value={g.id}>{g.name} ({onFloor} on floor)</option>;
                            })}
                        </select>
                    )}

                    {sendState.target_mode === 'select' && (<>
                        <input value={selectSearch} onChange={e => setSelectSearch(e.target.value)}
                            placeholder="Search by name or ID…" style={{ ...mdInput, marginBottom: '5px' }} />
                        <div style={{ border: '1px solid #334155', borderRadius: '6px', overflow: 'hidden', maxHeight: '130px', overflowY: 'auto', marginBottom: '8px' }}>
                            {mergedPeople.filter(ip => {
                                if (!selectSearch) return true;
                                const q = selectSearch.toLowerCase();
                                const asset = gpsAssets.find(a => a.id === ip.device_user);
                                return ip.device_user_name?.toLowerCase().includes(q) || String(asset?.identification_number ?? '').toLowerCase().includes(q);
                            }).map(ip => {
                                const asset = gpsAssets.find(a => a.id === ip.device_user);
                                return (
                                    <label key={ip.device_user} style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '6px 10px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #1E293B', color: '#CBD5E1' }}>
                                        <input type="checkbox"
                                            checked={sendState.selected_ids.includes(ip.device_user)}
                                            onChange={() => setSendState(s => {
                                                const ids = s.selected_ids;
                                                return { ...s, selected_ids: ids.includes(ip.device_user) ? ids.filter(i => i !== ip.device_user) : [...ids, ip.device_user] };
                                            })}
                                            style={{ accentColor: '#818CF8' }} />
                                        <div>
                                            <div>{ip.device_user_name}</div>
                                            {asset?.identification_number && <div style={{ fontSize: '10px', color: '#64748B' }}>{asset.identification_number}</div>}
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    </>)}

                    <div style={{ borderTop: '1px solid #334155', margin: '10px 0 8px' }} />
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#94A3B8', marginBottom: '8px' }}>{t('gps.assets.action')}</div>
                    {targetGroups.length === 0 && <div style={{ fontSize: '12px', color: '#475569', marginBottom: '8px' }}>{t('gps.assets.selectTargetsFirst')}</div>}
                    {multipleTypes && (
                        <div style={{ marginBottom: '8px', padding: '6px 10px', borderRadius: '6px', background: '#1C1917', border: '1px solid #78350F', fontSize: '11px', color: '#FBBF24' }}>
                            {t('gps.assets.multipleDeviceTypesWarning', { count: targetGroups.length })}
                        </div>
                    )}
                    {targetGroups.map(g => renderActionSelector(g.device_type_id, g.device_type_name, g.asset_ids))}

                    {sendResult && (
                        <div style={{ marginTop: '8px', padding: '8px 10px', borderRadius: '6px', background: sendResult.error ? '#2D0707' : '#072D07', border: `1px solid ${sendResult.error ? '#7F1D1D' : '#14532D'}`, fontSize: '12px', color: sendResult.error ? '#FCA5A5' : '#86EFAC' }}>
                            {sendResult.error
                                ? sendResult.error
                                : `${t('gps.assets.actionSentTo')} ${sendResult.executed} person(s)${sendResult.errors?.length ? ` (${sendResult.errors.length} errors)` : ''}`
                            }
                            {sendResult.errors?.length > 0 && (
                                <ul style={{ margin: '4px 0 0', paddingLeft: '14px' }}>
                                    {sendResult.errors.map((e, i) => <li key={i}>{e.asset}: {e.error}</li>)}
                                </ul>
                            )}
                        </div>
                    )}
                    <button onClick={handleSendAction} disabled={sendLoading}
                        style={{ ...smallBtn('#16A34A'), width: '100%', marginTop: '10px', justifyContent: 'center', opacity: sendLoading ? 0.6 : 1 }}>
                        <FontAwesomeIcon icon={faPaperPlane} />
                        {sendLoading ? t('gps.assets.sending') : t('gps.indoor.sendAction')}
                    </button>
                </div>
            </div>

            {/* ── Floor plan area ── */}
            <div style={{ flex: 1, overflow: 'hidden', background: '#0F172A', position: 'relative' }}>
                {editMode && pendingItem && (
                    <div style={{ position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)', zIndex: 10, background: editMode === 'beacon' ? '#4F46E5' : '#0F766E', color: '#fff', borderRadius: '8px', padding: '6px 14px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: '600', boxShadow: '0 2px 8px rgba(0,0,0,0.4)', whiteSpace: 'nowrap' }}>
                        Placing: {pendingItem.name || pendingItem.camera_name}
                        <button onClick={() => { setEditMode(null); setPendingItem(null); setPendingPlacement(null); }}
                            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '4px', color: '#fff', cursor: 'pointer', fontSize: '12px', padding: '2px 8px' }}>
                            <FontAwesomeIcon icon={faXmark} /> Cancel
                        </button>
                    </div>
                )}
                {floor?.floor_plan_url ? (
                    <IndoorFloorMap
                        floor={floor}
                        beacons={beacons}
                        cameras={cameras}
                        positions={mqttPositions}
                        indoorPeople={indoorPositions}
                        gpsAssets={gpsAssets}
                        editMode={editMode}
                        onPlaceItem={onPlaceItem}
                        pendingPlacement={pendingPlacement}
                        setPendingPlacement={setPendingPlacement}
                        onReposition={onReposition}
                        onRemove={onRemove}
                    />
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#475569', fontSize: '14px' }}>
                        {selectedFloor ? 'No floor plan uploaded for this floor.' : 'Select a building and floor to view the floor plan.'}
                    </div>
                )}
            </div>
        </div>
    );
}
